import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task, timeout } from 'ember-concurrency';
import { showDistance } from 'frontend-utils';
import type { Hotelinfo } from 'frontend-utils/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export interface HotelInfoResponse {
  brand_code: null;
  hotelinfo_id: number;
  latitude: number;
  longitude: number;
  name: string;
  stars: number;
}

interface AutocompleteHotelinfosResponse {
  meta: {
    results_count: number;
    results_page: number;
    results_per_page: number;
  };
  results: HotelInfoResponse[];
}

interface SettingsCompetitorsInputAutocompleteCompetitorSignature {
  Args: {
    excludedHotelinfoIds: string[];
    isDisabled?: boolean;
    placeholder?: string;
    searchQuery?: string;
    selectHotelinfo: (hotelinfo: Hotelinfo) => void;
    selectedHotelinfo: Hotelinfo | null;
  };
  Element: HTMLDivElement;
}

export default class SettingsCompetitorsInputAutocompleteCompetitor extends Component<SettingsCompetitorsInputAutocompleteCompetitorSignature> {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked hotelinfos: HotelInfoResponse[] = [];
  @tracked numberOfResults: number = 0;
  @tracked page: number = 0;
  @tracked selectedHotelinfo: Hotelinfo | null = null;
  @tracked totalPages: number = 0;

  fetchHotelinfosTask = task(async (searchQuery, page): Promise<void> => {
    const { hotel } = this.hotelAndMarketService;

    const ownHotelinfoId = hotel.hotelinfo.id;

    const data = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      page,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      q: searchQuery,
      subscription_id: hotel.id,
      around_hotel_info_id: ownHotelinfoId,
      exclude_hotelinfo_ids: [
        ownHotelinfoId,
        ...this.args.excludedHotelinfoIds,
      ],
    };

    const url = '/autocomplete/hotelinfos/';

    try {
      const result: AutocompleteHotelinfosResponse = await this.ajax.request(
        url,
        {
          method: 'GET',
          data,
        },
      );

      this.hotelinfos = [...(this.hotelinfos ?? []), ...result.results];

      this.numberOfResults = result.meta.results_count;
      this.totalPages = Math.ceil(
        result.meta.results_count / result.meta.results_per_page,
      );
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  });

  handleSearchTask = task(async (searchQuery) => {
    this.page = 0;
    this.hotelinfos = [];

    await timeout(300);

    return this.fetchHotelinfosTask.perform(searchQuery, this.page);
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    const { hotel, user } = this.hotelAndMarketService;

    return this.hotelinfos.map((hotelinfo) => {
      const distance = showDistance(hotel.hotelinfo, hotelinfo, user.locale);

      return {
        hotelinfo,
        label: hotelinfo.name,
        unsafe_subLabel: distance,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOption() {
    if (!this.args.selectedHotelinfo) {
      return null;
    }

    return {
      hotelinfo: this.args.selectedHotelinfo,
      label: this.args.selectedHotelinfo.name,
    };
  }

  @action loadMore(): void {
    if (this.page < this.totalPages) {
      this.page += 1;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchHotelinfosTask.perform(this.args.searchQuery, this.page);
    }
  }

  @action onCloseSearch(): void {
    this.hotelinfos = [];
    this.page = 0;
    this.totalPages = 0;
    this.numberOfResults = 0;
  }

  @action onOpen(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.handleSearchTask.perform(this.args.searchQuery);
  }

  @action selectHotelinfo(option: { hotelinfo: Hotelinfo }): void {
    this.args.selectHotelinfo(option.hotelinfo);
  }

  <template>
    <PrismComboSelectInput
      @dataA11yDialogIgnoreFocusTrap={{true}}
      @disabled={{@isDisabled}}
      @isSearchable={{true}}
      @isSecondary={{true}}
      @onChange={{this.selectHotelinfo}}
      @onClose={{this.onCloseSearch}}
      @onOpen={{this.onOpen}}
      @onSearch={{this.handleSearchTask.perform}}
      @options={{this.options}}
      @placeholder={{@placeholder}}
      @unsafe_onLoadMore={{this.loadMore}}
      @value={{this.selectedOption}}
      data-test-id="input-autocomplete-competitor"
      ...attributes
    />
  </template>
}
